/** @jsx jsx */
import { Button, Modal, Spacer, TextGroupMedium } from "@cd2/components"
import { css, jsx } from "@emotion/core"
import React, { useEffect, useRef, useState } from "react"
import { useGlobalState } from "./GlobalStateProvider"

export const AUTO_LOGOUT_DEFAULT = 60 * 20 * 1000 // 20 minutes

export function InactivtyMonitor() {
  const { signedIn, signOut } = useGlobalState()

  const [shouldShowModal, setShouldShowModal] = useState(false)
  const ticks = useRef(0)

  const autoLogoutSecondsRef = useRef<number | null>(null)

  useEffect(() => {
    if (signedIn) {
      // you can't poll for mouse coords and you have to attach an event listener to get that info
      window.addEventListener("mousemove", handleMouseMove, false)
      const interval = setInterval(tick, 1)

      return () => {
        window.removeEventListener("mousemove", handleMouseMove)
        clearInterval(interval)

        handleClear()
      }
    }
  }, [signedIn])

  function handleMouseMove() {
    ticks.current = 0
  }

  function handleClear() {
    setShouldShowModal(false)
    ticks.current = 0
  }

  function getWarningTime(logoutTime: number) {
    return logoutTime - 300 <= 0 ? logoutTime / 2 : logoutTime - 300
  }

  function tick() {
    ticks.current += 1
    // use users logout timer or default
    const logoutTime = AUTO_LOGOUT_DEFAULT
    // warn 5 mins before, unless logout timer is < 5 minutes, then use 50% of logout time
    const warningTime = getWarningTime(logoutTime)

    if (ticks.current >= warningTime) {
      setShouldShowModal(true)
    }

    // 30 MINUTES
    if (ticks.current >= logoutTime) {
      signOut().catch(e => {
        if (
          (e instanceof Error && e.message === "logoutNotAuthenticated") ||
          (typeof e === "object" && e?.type === "logoutNotAuthenticated")
        ) {
          // Can't reproduce this, but it's happening live. Regardless, it's a success case.
          return
        }
        throw e
      })
    }
  }

  function getWarningDurationTitle() {
    const warningTime = secondsToHms(
      getWarningTime(autoLogoutSecondsRef.current || AUTO_LOGOUT_DEFAULT),
    )
    const hoursText = warningTime.h === 1 ? "hour" : "hours"
    const hoursStr = warningTime.h > 0 ? `${warningTime.h} ${hoursText} and ` : ""

    const minutesText = warningTime.m === 1 ? "minute" : "minutes"
    const minutesStr = `${warningTime.m} ${minutesText}`
    // produce text like: 1 hour and 5 minutes // 5 minutes
    return `${hoursStr}${minutesStr}`
  }

  function getWarningDurationBody() {
    const logoutTime = autoLogoutSecondsRef.current || AUTO_LOGOUT_DEFAULT
    const duration = getWarningTime(autoLogoutSecondsRef.current || AUTO_LOGOUT_DEFAULT)

    const warningTime = secondsToHms(logoutTime - duration)
    const hoursText = warningTime.h === 1 ? "hour" : "hours"
    const hoursStr = warningTime.h > 0 ? `${warningTime.h} ${hoursText} and ` : ""

    const minutesText = warningTime.m === 1 ? "minute" : "minutes"
    const minutesStr = `${warningTime.m} ${minutesText}`
    // produce text like: 1 hour and 5 minutes // 5 minutes
    return `${hoursStr}${minutesStr}`
  }

  return (
    <Modal
      isOpen={!!shouldShowModal}
      onRequestClose={handleClear}
      width={450}
      css={css`
        max-width: calc(100% - 16px);
        overflow: initial;
      `}
      render={() => (
        <React.Fragment>
          <TextGroupMedium
            heading={`You have been inactive for ${getWarningDurationTitle()}`}
            body={`You will be automatically logged out if you stay inactive for another ${getWarningDurationBody()}`}
            theme={{ heading: { size: "small", blockSpacing: 0.5 } }}
          />
          <Spacer size={24} />
          <Button onClick={handleClear}>Remain logged in</Button>
        </React.Fragment>
      )}
    />
  )
}

export function secondsToHms(d: number) {
  const h = Math.floor(d / 3600)
  const m = Math.floor((d % 3600) / 60)
  const s = Math.floor((d % 3600) % 60)

  return { h, m, s }
}
